<template>
  <div class="dashboard-page">
    <section class="dashboard-hero">
      <div>
        <div class="dashboard-eyebrow">OPERATIONS CENTER</div>
        <h1 class="dashboard-title">{{ greeting }}，今天重点关注 {{ visiblePendingTasks }} 项待办</h1>
        <p class="dashboard-subtitle">{{ todayText }} · 用户、内容、记录与审核数据实时汇总</p>
      </div>
      <button class="ghost-button dashboard-refresh" type="button" :disabled="loading" @click="loadOverview">
        {{ loading ? "刷新中..." : "刷新数据" }}
      </button>
    </section>

    <div v-if="errorMessage" class="dashboard-error">
      <span>{{ errorMessage }}</span>
      <button type="button" class="dashboard-retry" @click="loadOverview">重试</button>
    </div>

    <section class="metric-grid">
      <article v-for="item in metricCards" :key="item.key" class="metric-card" :class="`metric-card--${item.tone}`">
        <div class="metric-card__top">
          <span class="metric-card__label">{{ item.label }}</span>
          <span class="metric-card__icon">{{ item.icon }}</span>
        </div>
        <div class="metric-card__value">{{ item.value }}</div>
        <div class="metric-card__foot">
          <span>{{ item.hint }}</span>
          <span v-if="item.growth !== null" class="metric-card__growth" :class="{ 'metric-card__growth--down': item.growth < 0 }">
            {{ item.growth > 0 ? "+" : "" }}{{ item.growth }}%
          </span>
        </div>
      </article>
    </section>

    <section class="dashboard-main-grid">
      <article class="dashboard-panel todo-panel">
        <div class="panel-head">
          <div>
            <h2>运营待办</h2>
            <p>优先处理会影响用户体验和内容安全的事项</p>
          </div>
          <span class="panel-count">{{ visiblePendingTasks }}</span>
        </div>
        <div class="todo-list">
          <button
            v-for="item in visibleTodos"
            :key="item.key"
            type="button"
            class="todo-row"
            :class="`todo-row--${item.tone}`"
            @click="openPath(item.path)"
          >
            <span class="todo-row__dot"></span>
            <span class="todo-row__copy">
              <strong>{{ item.title }}</strong>
              <small>{{ item.description }}</small>
            </span>
            <span class="todo-row__count">{{ item.count }}</span>
            <span class="todo-row__arrow">›</span>
          </button>
        </div>
      </article>

      <article class="dashboard-panel trend-panel">
        <div class="panel-head">
          <div>
            <h2>近 7 日运营趋势</h2>
            <p>新增用户、广场内容与宠物记录</p>
          </div>
          <div class="trend-legend">
            <span><i class="legend-dot legend-dot--user"></i>用户</span>
            <span><i class="legend-dot legend-dot--post"></i>内容</span>
            <span><i class="legend-dot legend-dot--record"></i>记录</span>
          </div>
        </div>
        <div class="trend-chart">
          <div v-for="point in trends" :key="point.date" class="trend-column">
            <div class="trend-bars">
              <i class="trend-bar trend-bar--user" :style="barStyle(point.users)"></i>
              <i class="trend-bar trend-bar--post" :style="barStyle(point.posts)"></i>
              <i class="trend-bar trend-bar--record" :style="barStyle(point.records)"></i>
            </div>
            <span class="trend-label">{{ point.label }}</span>
          </div>
        </div>
      </article>
    </section>

    <section class="dashboard-bottom-grid">
      <article class="dashboard-panel recent-panel">
        <div class="panel-head">
          <div>
            <h2>最新动态</h2>
            <p>用户注册、社区内容和用户反馈</p>
          </div>
        </div>
        <div v-if="recentItems.length" class="recent-list">
          <button v-for="item in recentItems" :key="item.type + item.occurredAt + item.detail" type="button" class="recent-row" @click="openPath(item.path)">
            <span class="recent-type" :class="`recent-type--${item.type.toLowerCase()}`">{{ typeLabel(item.type) }}</span>
            <span class="recent-copy">
              <strong>{{ item.title }}</strong>
              <small>{{ item.detail }}</small>
            </span>
            <time>{{ formatTime(item.occurredAt) }}</time>
          </button>
        </div>
        <div v-else class="empty-copy">暂时没有最新动态</div>
      </article>

      <article class="dashboard-panel quick-panel">
        <div class="panel-head">
          <div>
            <h2>快捷操作</h2>
            <p>常用运营入口</p>
          </div>
        </div>
        <div class="quick-grid">
          <button v-for="item in visibleQuickActions" :key="item.path" class="quick-card" type="button" @click="openPath(item.path)">
            <strong>{{ item.title }}</strong>
            <span>{{ item.description }}</span>
            <i>›</i>
          </button>
        </div>
      </article>
    </section>
  </div>
</template>

<script>
import { fetchDashboardOverview } from "../api/dashboard"
import { hasAdminPermission } from "../utils/adminSession"

const emptyMetrics = () => ({
  totalUsers: 0,
  newUsersToday: 0,
  activeUsersToday: 0,
  totalPets: 0,
  newPetsToday: 0,
  recordsToday: 0,
  totalPosts: 0,
  postsToday: 0,
  interactionsToday: 0,
  pendingTasks: 0,
  userGrowthRate: 0,
  postGrowthRate: 0,
  recordGrowthRate: 0
})

export default {
  name: "DashboardView",
  data() {
    return {
      loading: false,
      errorMessage: "",
      metrics: emptyMetrics(),
      todos: [],
      trends: [],
      recentItems: [],
      quickActions: []
    }
  },
  computed: {
    greeting() {
      const hour = new Date().getHours()
      if (hour < 6) return "夜深了"
      if (hour < 12) return "早上好"
      if (hour < 18) return "下午好"
      return "晚上好"
    },
    todayText() {
      return new Intl.DateTimeFormat("zh-CN", {
        year: "numeric",
        month: "long",
        day: "numeric",
        weekday: "long"
      }).format(new Date())
    },
    metricCards() {
      return [
        {
          key: "users",
          label: "用户总量",
          icon: "用",
          value: this.formatNumber(this.metrics.totalUsers),
          hint: `今日新增 ${this.metrics.newUsersToday}`,
          growth: Number(this.metrics.userGrowthRate || 0),
          tone: "blue"
        },
        {
          key: "active",
          label: "今日活跃用户",
          icon: "活",
          value: this.formatNumber(this.metrics.activeUsersToday),
          hint: "有记录、发帖或反馈",
          growth: null,
          tone: "green"
        },
        {
          key: "pets",
          label: "宠物档案",
          icon: "宠",
          value: this.formatNumber(this.metrics.totalPets),
          hint: `今日新增 ${this.metrics.newPetsToday}`,
          growth: null,
          tone: "amber"
        },
        {
          key: "records",
          label: "今日记录",
          icon: "记",
          value: this.formatNumber(this.metrics.recordsToday),
          hint: "照顾、体重、排泄与蜕皮",
          growth: Number(this.metrics.recordGrowthRate || 0),
          tone: "purple"
        },
        {
          key: "posts",
          label: "广场内容",
          icon: "广",
          value: this.formatNumber(this.metrics.totalPosts),
          hint: `今日新增 ${this.metrics.postsToday}`,
          growth: Number(this.metrics.postGrowthRate || 0),
          tone: "rose"
        },
        {
          key: "interactions",
          label: "今日互动",
          icon: "互",
          value: this.formatNumber(this.metrics.interactionsToday),
          hint: "点赞、评论与收藏",
          growth: null,
          tone: "slate"
        }
      ]
    },
    visibleQuickActions() {
      return this.quickActions.filter(item => !item.permission || hasAdminPermission(item.permission))
    },
    visibleTodos() {
      return this.todos.filter(item => !item.permission || hasAdminPermission(item.permission))
    },
    visiblePendingTasks() {
      return this.visibleTodos.reduce((total, item) => total + Number(item.count || 0), 0)
    },
    trendMax() {
      return Math.max(
        1,
        ...this.trends.flatMap(item => [
          Number(item.users || 0),
          Number(item.posts || 0),
          Number(item.records || 0)
        ])
      )
    }
  },
  created() {
    this.loadOverview()
  },
  methods: {
    async loadOverview() {
      if (this.loading) return
      this.loading = true
      this.errorMessage = ""
      try {
        const res = await fetchDashboardOverview()
        const data = res.data || {}
        this.metrics = { ...emptyMetrics(), ...(data.metrics || {}) }
        this.todos = Array.isArray(data.todos) ? data.todos : []
        this.trends = Array.isArray(data.trends) ? data.trends : []
        this.recentItems = Array.isArray(data.recentItems) ? data.recentItems : []
        this.quickActions = Array.isArray(data.quickActions) ? data.quickActions : []
      } catch (error) {
        this.errorMessage = error.message || "工作台数据加载失败"
      } finally {
        this.loading = false
      }
    },
    openPath(path) {
      if (path) this.$router.push(path)
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("zh-CN")
    },
    barStyle(value) {
      const height = Math.max(3, Math.round(Number(value || 0) / this.trendMax * 112))
      return { height: `${height}px` }
    },
    formatTime(value) {
      if (!value) return "--"
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value).slice(5, 16)
      const diff = Date.now() - date.getTime()
      if (diff >= 0 && diff < 60 * 1000) return "刚刚"
      if (diff >= 0 && diff < 60 * 60 * 1000) return `${Math.floor(diff / 60000)} 分钟前`
      if (diff >= 0 && diff < 24 * 60 * 60 * 1000) return `${Math.floor(diff / 3600000)} 小时前`
      return `${date.getMonth() + 1}/${date.getDate()} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`
    },
    typeLabel(type) {
      const map = { USER: "用户", POST: "内容", FEEDBACK: "反馈" }
      return map[type] || "动态"
    }
  }
}
</script>

<style scoped>
.dashboard-page {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.dashboard-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 26px 28px;
  border: 1px solid #e3eaf2;
  border-radius: 24px;
  background:
    radial-gradient(circle at 100% 0, rgba(37, 99, 235, 0.13), transparent 36%),
    linear-gradient(135deg, #ffffff 0%, #f7faff 100%);
}

.dashboard-eyebrow {
  color: #2563eb;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.15em;
}

.dashboard-title {
  margin: 10px 0 0;
  color: #111827;
  font-size: 26px;
  line-height: 1.3;
}

.dashboard-subtitle {
  margin: 9px 0 0;
  color: #718096;
  font-size: 13px;
}

.dashboard-refresh {
  flex-shrink: 0;
}

.dashboard-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border: 1px solid #fecaca;
  border-radius: 14px;
  background: #fff7f7;
  color: #b42318;
  font-size: 13px;
}

.dashboard-retry {
  border: 0;
  background: transparent;
  color: #b42318;
  font-weight: 700;
  cursor: pointer;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 14px;
}

.metric-card {
  min-width: 0;
  padding: 18px;
  border: 1px solid #e8edf3;
  border-radius: 20px;
  background: #ffffff;
}

.metric-card__top,
.metric-card__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.metric-card__label {
  color: #6b7280;
  font-size: 13px;
}

.metric-card__icon {
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 800;
}

.metric-card__value {
  margin-top: 17px;
  color: #111827;
  font-size: 29px;
  font-weight: 800;
  letter-spacing: -0.03em;
}

.metric-card__foot {
  margin-top: 10px;
  color: #94a3b8;
  font-size: 11px;
}

.metric-card__growth {
  color: #16803c;
  font-weight: 700;
  white-space: nowrap;
}

.metric-card__growth--down {
  color: #dc2626;
}

.metric-card--blue .metric-card__icon { background: #eaf2ff; color: #2563eb; }
.metric-card--green .metric-card__icon { background: #e9f8ef; color: #15803d; }
.metric-card--amber .metric-card__icon { background: #fff4dd; color: #b7791f; }
.metric-card--purple .metric-card__icon { background: #f2eaff; color: #7e22ce; }
.metric-card--rose .metric-card__icon { background: #fff0f3; color: #be123c; }
.metric-card--slate .metric-card__icon { background: #eef2f6; color: #475569; }

.dashboard-main-grid,
.dashboard-bottom-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
  gap: 18px;
}

.dashboard-bottom-grid {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.08fr);
}

.dashboard-panel {
  min-width: 0;
  padding: 22px;
  border: 1px solid #e8edf3;
  border-radius: 22px;
  background: #ffffff;
}

.panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.panel-head h2 {
  margin: 0;
  color: #111827;
  font-size: 17px;
}

.panel-head p {
  margin: 6px 0 0;
  color: #94a3b8;
  font-size: 12px;
}

.panel-count {
  min-width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: #fff1f1;
  color: #b42318;
  font-size: 17px;
  font-weight: 800;
}

.todo-list {
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.todo-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border: 1px solid #edf1f6;
  border-radius: 15px;
  background: #fbfcfe;
  text-align: left;
  cursor: pointer;
}

.todo-row:hover {
  border-color: #d7e1ee;
  background: #f7faff;
}

.todo-row__dot {
  width: 9px;
  height: 9px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #cbd5e1;
}

.todo-row--danger .todo-row__dot { background: #ef4444; box-shadow: 0 0 0 5px #fee2e2; }
.todo-row--warning .todo-row__dot { background: #f59e0b; box-shadow: 0 0 0 5px #fef3c7; }
.todo-row--muted { opacity: 0.65; }

.todo-row__copy {
  min-width: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
}

.todo-row__copy strong {
  color: #1f2937;
  font-size: 13px;
}

.todo-row__copy small {
  overflow: hidden;
  color: #8a94a3;
  font-size: 11px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.todo-row__count {
  color: #111827;
  font-size: 20px;
  font-weight: 800;
}

.todo-row__arrow {
  color: #a6b0bf;
  font-size: 20px;
}

.trend-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  color: #8a94a3;
  font-size: 10px;
}

.trend-legend span {
  display: flex;
  align-items: center;
}

.legend-dot {
  width: 7px;
  height: 7px;
  margin-right: 4px;
  border-radius: 50%;
}

.legend-dot--user { background: #3b82f6; }
.legend-dot--post { background: #f59e0b; }
.legend-dot--record { background: #8b5cf6; }

.trend-chart {
  height: 160px;
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 8px;
  align-items: end;
  padding: 10px 6px 0;
  border-top: 1px solid #f1f4f8;
}

.trend-column {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
}

.trend-bars {
  height: 118px;
  display: flex;
  align-items: flex-end;
  gap: 3px;
}

.trend-bar {
  width: 7px;
  min-height: 3px;
  border-radius: 5px 5px 2px 2px;
}

.trend-bar--user { background: #3b82f6; }
.trend-bar--post { background: #f59e0b; }
.trend-bar--record { background: #8b5cf6; }

.trend-label {
  color: #9aa3af;
  font-size: 10px;
}

.recent-list {
  display: flex;
  flex-direction: column;
}

.recent-row {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 11px 2px;
  border: 0;
  border-bottom: 1px solid #f1f4f8;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.recent-row:last-child {
  border-bottom: 0;
}

.recent-type {
  width: 34px;
  height: 26px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 700;
}

.recent-type--user { background: #eaf2ff; color: #2563eb; }
.recent-type--post { background: #fff4dd; color: #b7791f; }
.recent-type--feedback { background: #fff0f3; color: #be123c; }

.recent-copy {
  min-width: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 3px;
}

.recent-copy strong {
  color: #273243;
  font-size: 12px;
}

.recent-copy small {
  overflow: hidden;
  color: #8f99a8;
  font-size: 11px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.recent-row time {
  color: #a3acb9;
  font-size: 10px;
  white-space: nowrap;
}

.quick-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.quick-card {
  position: relative;
  min-height: 86px;
  padding: 15px 34px 15px 15px;
  border: 1px solid #e9eef5;
  border-radius: 16px;
  background: linear-gradient(145deg, #ffffff, #f8fafc);
  text-align: left;
  cursor: pointer;
}

.quick-card:hover {
  border-color: #cad8e9;
  transform: translateY(-1px);
}

.quick-card strong {
  display: block;
  color: #243044;
  font-size: 13px;
}

.quick-card span {
  display: block;
  margin-top: 7px;
  color: #8c96a5;
  font-size: 11px;
  line-height: 1.5;
}

.quick-card i {
  position: absolute;
  right: 14px;
  top: 50%;
  color: #9ba6b5;
  font-size: 20px;
  font-style: normal;
  transform: translateY(-50%);
}

.empty-copy {
  padding: 30px 0;
  color: #9aa3af;
  text-align: center;
  font-size: 12px;
}

@media (max-width: 1280px) {
  .metric-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}

@media (max-width: 900px) {
  .dashboard-hero { align-items: flex-start; flex-direction: column; }
  .dashboard-main-grid,
  .dashboard-bottom-grid { grid-template-columns: 1fr; }
}

@media (max-width: 680px) {
  .metric-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .quick-grid { grid-template-columns: 1fr; }
}
</style>
